import { useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { EASE } from "./Reveal";
import { useLang } from "../../lib/lang";

const DISHES = [
  { k: "foul", name: "Mezze foul", price: "59", sv: "Långkokta favabönor med olivolja, tomat och persilja", en: "Slow-cooked fava beans with olive oil, tomato and parsley" },
  { k: "hummus", name: "Hummus", price: "59", sv: "Krämig hummus med tahini, citron och olivolja", en: "Creamy hummus with tahini, lemon and olive oil" },
  { k: "musbaha", name: "Musbaha", price: "59", sv: "Kikärtor i krämig röra med tahini, citron och kryddor", en: "Chickpeas in a creamy mix with tahini, lemon and spices" },
  { k: "fatteh", name: "Fatteh", price: "79", sv: "Krispigt pitabröd, kikärtor, yoghurt och kryddor", en: "Crispy pita bread, chickpeas, yogurt and spices" },
  { k: "lbspecial", name: "LB Special", price: "69", sv: "Kombination av hummus och favabönor, toppad med olivolja och persilja", en: "Hummus and fava beans combined, topped with olive oil and parsley" },
];

const TXT = {
  sv: { eyebrow: "Mezze", title: "Fem klassiker,", em: "en bit Levanten", note: "Alla rätter serveras med 2 färska pitabröd. Välj tillbehör: vinäger eller tahini (0 kr), grönsaker (+10 kr)." },
  en: { eyebrow: "Mezze", title: "Five classics,", em: "a taste of the Levant", note: "Every dish is served with 2 fresh pita breads. Add vinegar or tahini (0 kr), or vegetables (+10 kr)." },
};

export const DishCard = ({ name, price, src, desc, onOpen, i = 0, testId, animateOnMount = false }) => (
  <motion.button
    type="button"
    data-testid={testId}
    onClick={onOpen}
    initial={{ y: 40 }}
    {...(animateOnMount ? { animate: { y: 0 } } : { whileInView: { y: 0 }, viewport: { once: true, amount: 0.15 } })}
    transition={{ duration: 0.9, ease: EASE, delay: i * 0.06 }}
    className="spotlight group relative w-[72%] shrink-0 snap-start text-left sm:w-[42%] lg:w-auto"
  >
    <span className="arch relative block aspect-[3/4] overflow-hidden bg-[#2a0a10] shadow-[0_24px_50px_-24px_rgba(74,14,23,0.55)]">
      <img src={src} alt={name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110" />
      <span className="absolute right-3 top-[18%] rounded-full bg-[#721121] px-3 py-1.5 text-sm font-semibold text-white ring-1 ring-[#E9C46A]/40">{price} kr</span>
    </span>
    <span className="mt-4 block font-display text-2xl text-[#1C1917] transition-colors duration-300 group-hover:text-[#721121]">{name}</span>
    {desc && <span className="mt-1 block text-sm leading-relaxed text-[#57534E]">{desc}</span>}
  </motion.button>
);

export const DishLightbox = ({ dish, onClose }) => (
  <Dialog open={!!dish} onOpenChange={(o) => { if (!o) onClose(); }}>
    <DialogContent data-testid="dish-lightbox" className="max-w-2xl overflow-hidden border-0 bg-[#1a0508] p-0 text-white [&>button]:rounded-full [&>button]:bg-black/50 [&>button]:p-1.5 [&>button]:text-white [&>button]:opacity-100">
      {dish && (
        <>
          <img src={dish.src} alt={dish.name} className="aspect-[3/2] w-full bg-[#2a0a10] object-cover" />
          <div className="p-6">
            <div className="flex items-baseline justify-between gap-4">
              <DialogTitle className="font-display text-3xl font-medium">{dish.name}</DialogTitle>
              <span className="font-display text-2xl text-[#E9C46A]">{dish.price} kr</span>
            </div>
            <DialogDescription className="mt-2 text-sm text-white/80">{dish.desc || ""}</DialogDescription>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export const CARD_ROW = "-mx-5 mt-10 flex snap-x snap-mandatory scroll-pl-5 gap-4 overflow-x-auto px-5 pb-4 sm:-mx-10 sm:scroll-pl-10 sm:px-10 lg:mx-0 lg:grid lg:overflow-visible lg:px-0 lg:pb-0";

export const MezzeShowcase = () => {
  const { lang } = useLang();
  const t = TXT[lang];
  const [zoom, setZoom] = useState(null);
  return (
    <div data-testid="mezze-section" className="rounded-[32px] bg-[#F6EFE4] px-5 py-14 sm:px-10 lg:py-20">
      <p className="eyebrow text-[#721121]">{t.eyebrow}</p>
      <h3 className="font-display mt-4 text-4xl leading-[0.95] tracking-tight text-[#1C1917] sm:text-5xl">
        {t.title} <em className="italic text-[#721121]">{t.em}</em>
      </h3>
      <div data-lenis-prevent-touch className={`${CARD_ROW} lg:grid-cols-5`}>
        {DISHES.map((d, i) => (
          <DishCard
            key={d.k}
            i={i}
            testId={`mezze-card-${d.k}`}
            name={d.name}
            price={d.price}
            desc={d[lang]}
            src={`/mezze/${d.k}.webp`}
            onOpen={() => setZoom({ name: d.name, price: d.price, desc: d[lang], src: `/dish/z-${d.k}-lg.webp` })}
          />
        ))}
      </div>
      <p data-testid="mezze-note" className="mt-8 max-w-2xl text-sm text-[#57534E]">{t.note}</p>
      <DishLightbox dish={zoom} onClose={() => setZoom(null)} />
    </div>
  );
};
